import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import { getPost, BLOG } from "@/data/blog";
import SEO, { articleSchema } from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogDetail = () => {
  const { slug } = useParams();
  const [adminPost, setAdminPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/blog/${slug}`)
      .then(({ data }) => setAdminPost({ ...data, readTime: data.read_time }))
      .catch(() => setAdminPost(null))
      .finally(() => setLoading(false));
  }, [slug]);

  const staticPost = getPost(slug);
  const post = adminPost || staticPost;
  if (loading && !staticPost) return <div className="min-h-screen" />;
  if (!post) return <Navigate to="/blog" replace />;
  const related = BLOG.filter((p) => p.slug !== slug).slice(0, 3);

  return (
    <div data-testid="blog-detail-page">
      <SEO
        title={post.title}
        description={post.excerpt}
        keywords={`${post.category.toLowerCase()}, sanatan dharma, ${post.title.toLowerCase()}, indian rituals, puja tradition`}
        path={`/blog/${post.slug}`}
        image={post.image}
        jsonLd={articleSchema(post)}
      />
      <section className="mandala-bg border-b border-[#3B2A1A]/10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <Link to="/blog" className="text-xs uppercase tracking-[0.18em] text-[#8A7969] hover:text-[#3B2A1A] inline-flex items-center gap-2 mb-10" data-testid="back-to-blog">
            <ArrowLeft size={12} /> The Journal
          </Link>
          <p className="overline">{post.category} · {post.readTime} · {post.date}</p>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl text-[#3B2A1A] mt-5 leading-[1.05] tracking-tight" data-testid="post-title">{post.title}</h1>
          <p className="text-lg text-[#5C4A3A] mt-6 font-light leading-relaxed">{post.excerpt}</p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="aspect-[16/9] overflow-hidden bg-[#F5EFE3]" data-testid="post-hero-image">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover" />
          </div>
        </div>
      </section>

      <article className="py-12 lg:py-16">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6" data-testid="post-body">
          {post.body.map((para, i) => (
            <p key={i} className={`text-lg leading-[1.85] text-[#3B2A1A] font-light ${i === 0 ? "first-letter:font-serif-display first-letter:text-6xl first-letter:float-left first-letter:mr-3 first-letter:leading-[0.85] first-letter:text-[#C89D3C]" : ""}`}>
              {para}
            </p>
          ))}
          <div className="border-l-2 border-[#C89D3C] pl-5 mt-12">
            <p className="font-serif-display text-2xl text-[#3B2A1A] leading-snug">A trusted ritual is built one steady habit at a time.</p>
            <p className="overline mt-3">— Sanatan Dharma™</p>
          </div>
        </div>
      </article>

      <section className="py-20 lg:py-24 border-t border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-end mb-12">
            <div>
              <p className="overline">Continue Reading</p>
              <h2 className="font-serif-display text-3xl lg:text-4xl text-[#3B2A1A] mt-2">More from the Journal</h2>
            </div>
            <Link to="/blog" className="text-xs uppercase tracking-[0.18em] font-semibold text-[#3B2A1A] hover-underline hidden sm:inline-flex items-center gap-2" data-testid="all-articles-link">All Articles <ArrowRight size={12} /></Link>
          </div>
          <div className="grid md:grid-cols-3 gap-x-10 gap-y-12" data-testid="related-posts">
            {related.map((p) => (
              <Link to={`/blog/${p.slug}`} key={p.slug} className="group" data-testid={`related-post-${p.slug}`}>
                <div className="aspect-[4/3] overflow-hidden bg-[#F5EFE3]">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1500ms]" />
                </div>
                <p className="overline mt-5 text-[10px]">{p.category}</p>
                <h3 className="font-serif-display text-2xl text-[#3B2A1A] mt-3 leading-tight">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogDetail;
